import { cs } from "../utils/currency";
import React, { useState, useEffect, useRef } from "react";
import { X, Upload, Plus, MapPin, ChevronDown } from "lucide-react";
import api, { getAllPresetsAPI, getHotels, updateRestaurantMenuItem } from "../services/api";
import { getTokenInfo } from "../utils/auth";
import { toast } from "react-hot-toast";
import PrerequisitesDialog from "./PrerequisitesDialog";
import vegIcon from "../assests/Veg_symbol.png";
import nonvegIcon from "../assests/Non_veg_symbol.png";
import both from "../assests/both.png";

interface CategoryCode {
  _id: string;
  code: string;
  name: string;
  taxable: boolean;
}

interface Choice {
  label: string;
  price: number;
  default?: boolean;
  image?: string;
}

interface PortionOption {
  key: string;
  label: string;
  icon?: string;
}

interface CustomGroup {
  name: string;
  required: boolean;
  multiple?: boolean;
  choices: Choice[];
  noOfFreeToppings?: number;
  maxToppingsAllowed?: number;
  minAddons?: number;
  customized?: boolean;
  multipleAddons?: boolean;
  displayType?: "checkbox" | "quantity" | "portion" | "radio";
  quantityOptions?: number[];
  portionOptions?: PortionOption[];
  showPortionSelector?: boolean;
  fromPreset?: boolean;
  presetId?: string;
}

interface Choices {
  _id: string;
  label: string;
  price: number;
  default: boolean;
}

interface CustomizationOption {
  _id: string;
  name: string;
  required: boolean;
  multiple: boolean;
  multipleAddons?: boolean;
  choices: Choices[];
  noOfFreeToppings?: number;
  maxToppingsAllowed?: number;
  minAddons?: number;
  displayType?: "checkbox" | "quantity" | "portion" | "radio";
  quantityOptions?: number[];
  portionOptions?: PortionOption[];
  showPortionSelector?: boolean;
}

interface PresetItem {
  _id: string;
  name: string;
  description: string;
  categoryId: string;
  category: string;
  isActive: boolean;
  customizationOptions: CustomizationOption[];
}

interface Hotel {
  _id: string;
  hotelName: string;
  address: string;
}

interface EditingMenuItem {
  _id: string;
  name: string;
  price: number;
  discount?: number;
  category: string;
  categoryId: string | { _id: string };
  additionalCategoryIds?: (string | { _id: string })[];
  categoryCode?: string;
  imageUrl: string;
  description?: string;
  tags?: string[];
  customize?: CustomGroup[];
  foodType: "veg" | "non-veg" | "both";
  fulfillmentMethods?: ("pickup" | "delivery" | "self-ordering")[];
  kitchenName?: string;
  prepTime?: number;
}

interface RestaurantMenuEntry {
  _id: string;
  menuId: string;
  hotelId: string | { _id: string; hotelName: string };
  price?: number;
  availability: boolean;
  fulfillmentMethods?: ("pickup" | "delivery" | "self-ordering")[];
}

type FulfillmentMethod = "pickup" | "delivery" | "self-ordering";

interface LocationSettings {
  price: number | null;
  availability: boolean;
  fulfillmentMethods: FulfillmentMethod[];
}

const FULFILLMENT_METHOD_LABELS: Record<FulfillmentMethod, string> = {
  pickup: "Pickup",
  delivery: "Delivery",
  "self-ordering": "Self-Ordering",
};

type CategoryOption = { _id: string; name: string; categoryCode?: string | { _id: string } };

// Shared across dialog opens so reopening shows data instantly; refreshed in the background
const lookupCache: {
  presets?: PresetItem[];
  hotels?: Hotel[];
  codes?: CategoryCode[];
  categories?: CategoryOption[];
} = {};

interface AddMenuDialogProps {
  isOpen: boolean;
  onClose: () => void;
  categoryId: string;
  categoryName: string;
  categoryCodeId?: string;
  categoryCodes?: CategoryCode[];
  categories?: CategoryOption[];
  onMenuItemAdded: () => void;
  editingItem?: EditingMenuItem | null;
}

const AddMenuDialog: React.FC<AddMenuDialogProps> = ({
  isOpen,
  onClose,
  categoryId,
  categoryName,
  categoryCodeId,
  categoryCodes: categoriesCodesProp,
  categories: categoriesProp,
  onMenuItemAdded,
  editingItem,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    price: "",
    categoryCode: "",
    description: "",
    foodType: "veg" as "veg" | "non-veg" | "both",
    fulfillmentMethods: [] as ("pickup" | "delivery" | "self-ordering")[],
    tags: "",
    imageUrl: "",
    customize: [] as CustomGroup[],
    selectedLocations: [] as string[], // For new items - store selected hotel IDs
    categoryId: categoryId, // Main category (can be changed while editing)
    additionalCategoryIds: [] as string[],
    kitchenName: "",
    prepTime: 10,
  });

  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Customization group modal state
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [editingGroupIndex, setEditingGroupIndex] = useState<number | null>(
    null,
  );
  const [groupDraft, setGroupDraft] = useState<CustomGroup>({
    name: "",
    required: false,
    multiple: false,
    choices: [],
    noOfFreeToppings: undefined,
    maxToppingsAllowed: undefined,
    minAddons: undefined,
    customized: true,
    multipleAddons: false,
    displayType: "checkbox",
    quantityOptions: [0, 1, 2, 3],
    portionOptions: [
      { key: "left", label: "Left" },
      { key: "whole", label: "Whole" },
      { key: "right", label: "Right" },
    ],
    showPortionSelector: false,
    fromPreset: false,
  });
  const [groupErrors, setGroupErrors] = useState<string[]>([]);

  // Presets state
  const [presetData, setPresetData] = useState<PresetItem[]>(lookupCache.presets || []);
  const [selectedPresetIds, setSelectedPresetIds] = useState<string[]>([]);
  const [open, setOpen] = useState(false);

  // Hotels state
  const [hotels, setHotels] = useState<Hotel[]>(lookupCache.hotels || []);
  const [restaurantMenuEntries, setRestaurantMenuEntries] = useState<
    RestaurantMenuEntry[]
  >([]);
  const [locationUpdating, setLocationUpdating] = useState<string | null>(null);

  // Location settings state
  const tokenInfo = getTokenInfo();
  const isMasterAdmin = tokenInfo?.role === "master_admin";
  // Location admins only view item details; they can toggle availability and
  // fulfillment methods for their own location.
  const readOnly = !isMasterAdmin;
  const myHotelId = (tokenInfo as any)?.restaurantId as string | undefined;
  const [locationSettingsMap, setLocationSettingsMap] = useState<{
    [hotelId: string]: LocationSettings;
  }>({});
  const [expandedHotelRow, setExpandedHotelRow] = useState<string | null>(null);
  const [savingLocation, setSavingLocation] = useState<string | null>(null);

  // Choice image upload state
  const [, setUploadingChoices] = useState<{
    [key: string]: boolean;
  }>({});

  // Category codes state
  const [categoryCodes, setCategoryCodes] = useState<CategoryCode[]>([]);
  const [categoryDefaultCodeId, setCategoryDefaultCodeId] = useState<string>("");
  // All categories, for picking additional categories to list the item under
  const [allCategories, setAllCategories] = useState<CategoryOption[]>(
    categoriesProp || lookupCache.categories || [],
  );
  // Item the form was last filled from — avoids filling it twice on open
  const populatedItemRef = useRef<EditingMenuItem | null | undefined>(undefined);
  const defaultCodeRef = useRef<string>("");
  // true after Effect 1 has resolved — prevents Effect 2 from firing before data is ready
  const dataReadyRef = useRef<boolean>(false);

  const getMatchedPresetIds = (
    presets: PresetItem[],
    customGroups: CustomGroup[],
  ): string[] => {
    const groupNames = new Set(
      customGroups.map((g) => g.name.trim().toLowerCase()),
    );

    return presets
      .filter((preset) =>
        preset.customizationOptions.some((opt) =>
          groupNames.has(opt.name.trim().toLowerCase()),
        ),
      )
      .map((preset) => preset._id);
  };

  const populateForm = (item: EditingMenuItem | null | undefined, defaultCodeId: string) => {
    populatedItemRef.current = item;
    if (item) {
      setFormData({
        name: item.name,
        price: item.price.toString(),
        categoryCode: item.categoryCode || defaultCodeId,
        description: item.description || "",
        foodType: item.foodType,
        fulfillmentMethods: item.fulfillmentMethods || [],
        tags: item.tags?.join(", ") || "",
        imageUrl: item.imageUrl,
        customize: item.customize || [],
        selectedLocations: [],
        categoryId:
          (typeof item.categoryId === "object" ? item.categoryId?._id : item.categoryId) ||
          categoryId,
        additionalCategoryIds: (item.additionalCategoryIds || []).map((c) =>
          typeof c === "object" ? c._id : c,
        ),
        kitchenName: item.kitchenName || "",
        prepTime: item.prepTime || 10,
      });
      const customizeGroups = item.customize || [];
      const presetIdBased = Array.from(
        new Set(
          customizeGroups
            .filter((g) => g.fromPreset && g.presetId)
            .map((g) => g.presetId!),
        ),
      );
      const matchedPresetIds =
        presetIdBased.length > 0
          ? presetIdBased
          : getMatchedPresetIds(presetData, customizeGroups);
      setSelectedPresetIds(matchedPresetIds);
      api.get(`/restaurant-menu/by-menu/${item._id}`)
        .then((res) => {
          if (res.data.success) {
            const entries: RestaurantMenuEntry[] = res.data.data || [];
            setRestaurantMenuEntries(entries);
            const settingsMap: { [hotelId: string]: LocationSettings } = {};
            entries.forEach((entry) => {
              const hotelId = typeof entry.hotelId === "object" ? entry.hotelId._id : entry.hotelId;
              if (hotelId) {
                settingsMap[hotelId] = {
                  price: entry.price ?? null,
                  availability: entry.availability ?? true,
                  fulfillmentMethods: (entry.fulfillmentMethods && entry.fulfillmentMethods.length > 0
                    ? entry.fulfillmentMethods
                    : ["pickup", "delivery", "self-ordering"]) as FulfillmentMethod[],
                };
              }
            });
            setLocationSettingsMap(settingsMap);
          }
        })
        .catch(() => {
          setRestaurantMenuEntries([]);
          setLocationSettingsMap({});
        });
    } else {
      resetForm();
      setRestaurantMenuEntries([]);
      setFormData((prev) => ({ ...prev, categoryCode: defaultCodeId }));
    }
  };

  // Effect 1: fires on open/category change. Fills the form immediately from the
  // data already in hand (the item + parent-supplied codes/categories), then
  // refreshes lookups in the background so the fields never sit empty.
  useEffect(() => {
    if (!isOpen) {
      dataReadyRef.current = false;
      defaultCodeRef.current = "";
      populatedItemRef.current = undefined;
      return;
    }

    const findDefaultCode = (cats: CategoryOption[]) => {
      const matched = cats.find((c) => c._id === categoryId);
      return (
        (typeof matched?.categoryCode === "object"
          ? matched.categoryCode?._id
          : matched?.categoryCode) || ""
      );
    };

    const knownCategories = categoriesProp || lookupCache.categories || [];
    const initialDefault = categoryCodeId || findDefaultCode(knownCategories);
    setCategoryCodes(categoriesCodesProp || lookupCache.codes || []);
    setAllCategories(knownCategories);
    defaultCodeRef.current = initialDefault;
    setCategoryDefaultCodeId(initialDefault);
    dataReadyRef.current = true;
    populateForm(editingItem, initialDefault);

    // Background refreshes — the form is already usable
    getAllPresetsAPI()
      .then((res) => {
        const data = Array.isArray(res.data?.data) ? res.data.data : Array.isArray(res.data) ? res.data : [];
        lookupCache.presets = data;
        setPresetData(data);
      })
      .catch(() => {});
    getHotels()
      .then((res) => {
        lookupCache.hotels = res.data.data || [];
        setHotels(lookupCache.hotels!);
      })
      .catch(() => {});
    if (!categoriesCodesProp) {
      api
        .get<CategoryCode[]>("/codes")
        .then((res) => {
          if (!Array.isArray(res.data)) return;
          lookupCache.codes = res.data;
          setCategoryCodes(res.data);
        })
        .catch(() => {});
    }
    if (!categoriesProp) {
      api
        .get("/categories")
        .then((res) => {
          const cats: CategoryOption[] = res.data?.data || [];
          lookupCache.categories = cats;
          setAllCategories(cats);
          if (categoryCodeId || defaultCodeRef.current) return;
          const defaultCodeId = findDefaultCode(cats);
          if (!defaultCodeId) return;
          defaultCodeRef.current = defaultCodeId;
          setCategoryDefaultCodeId(defaultCodeId);
          setFormData((prev) =>
            prev.categoryCode ? prev : { ...prev, categoryCode: defaultCodeId },
          );
        })
        .catch(() => {});
    }
  }, [isOpen, categoryId]);

  // Effect 2: fires when editingItem changes while the dialog is already open.
  useEffect(() => {
    if (!isOpen || !dataReadyRef.current) return;
    if (populatedItemRef.current === editingItem) return;
    populateForm(editingItem, defaultCodeRef.current);
  }, [editingItem]);

  const resetForm = () => {
    setFormData({
      name: "",
      price: "",
      categoryCode: "",
      description: "",
      foodType: "veg",
      fulfillmentMethods: [],
      tags: "",
      imageUrl: "",
      customize: [],
      selectedLocations: [],
      categoryId: categoryId,
      additionalCategoryIds: [],
      kitchenName: "",
      prepTime: 10,
    });
    setFile(null);
    setSelectedPresetIds([]);
    setGroupErrors([]);
    setRestaurantMenuEntries([]);
    setLocationSettingsMap({});
    setExpandedHotelRow(null);
  };

  const handleChoiceImageUpload = async (
    file: File,
    choiceKey: string,
  ): Promise<string> => {
    const formDataObj = new FormData();
    formDataObj.append("image", file);
    setUploadingChoices((prev) => ({ ...prev, [choiceKey]: true }));

    try {
      const response = await api.post<{ url: string }>(
        "/upload-image",
        formDataObj,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );
      return response.data.url;
    } catch (err) {
      console.error("Error uploading image", err);
      throw new Error("Image upload failed");
    } finally {
      setUploadingChoices((prev) => ({ ...prev, [choiceKey]: false }));
    }
  };

  const uploadImage = async (): Promise<string> => {
    if (!file) return formData.imageUrl;
    const formDataObj = new FormData();
    formDataObj.append("image", file);
    setUploading(true);

    try {
      const res = await api.post<{ url: string }>(
        "/upload-image",
        formDataObj,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );
      setUploading(false);
      return res.data.url;
    } catch (err) {
      setUploading(false);
      console.error("Image upload failed:", err);
      toast.error("Failed to upload image");
      return "";
    }
  };

  const handleSave = (selectedIds: string[]) => {
    setSelectedPresetIds(selectedIds);

    const selectedPresets = selectedIds
      .map((id) => presetData.find((p) => p._id === id))
      .filter(Boolean) as PresetItem[];

    const presetGroups: CustomGroup[] = selectedPresets.flatMap((preset) =>
      preset.customizationOptions.map((option) => ({
        name: option.name,
        required: option.required,
        multiple: option.multiple,
        choices: option.choices.map((choice) => ({
          label: choice.label,
          price: choice.price,
          default: choice.default,
        })),
        noOfFreeToppings: option.noOfFreeToppings,
        maxToppingsAllowed: option.maxToppingsAllowed,
        minAddons: option.minAddons,
        multipleAddons: option.multipleAddons || false,
        displayType: option.displayType || "checkbox",
        quantityOptions: option.quantityOptions || [0, 1, 2, 3],
        portionOptions: option.portionOptions || [
          { key: "left", label: "Left" },
          { key: "whole", label: "Whole" },
          { key: "right", label: "Right" },
        ],
        showPortionSelector: option.showPortionSelector || false,
        fromPreset: true,
        presetId: preset._id,
      })),
    );

    const normalizeGroupName = (name: string) => name.trim().toLowerCase();
    const getPresetGroupKey = (group: CustomGroup) =>
      `${group.presetId || ""}::${normalizeGroupName(group.name)}`;

    setFormData((prev) => {
      const filteredCustomize = prev.customize.filter((group) => {
        if (group.fromPreset) {
          if (group.presetId) {
            return selectedIds.includes(group.presetId);
          }
          return presetGroups.some(
            (presetGroup) =>
              normalizeGroupName(presetGroup.name) ===
              normalizeGroupName(group.name),
          );
        }
        return true;
      });

      const existingPresetKeys = new Set(
        filteredCustomize
          .filter((group) => group.fromPreset)
          .map(getPresetGroupKey),
      );
      const newGroups = presetGroups.filter(
        (group) => !existingPresetKeys.has(getPresetGroupKey(group)),
      );

      return {
        ...prev,
        customize: [...filteredCustomize, ...newGroups],
      };
    });
  };

  useEffect(() => {
    if (!isOpen || !editingItem || !presetData.length) return;

    const customizeGroups = formData.customize || [];
    const presetIdBased = Array.from(
      new Set(
        customizeGroups
          .filter((g) => g.fromPreset && g.presetId)
          .map((g) => g.presetId!),
      ),
    );
    const matchedPresetIds =
      presetIdBased.length > 0
        ? presetIdBased
        : getMatchedPresetIds(presetData, customizeGroups);

    setSelectedPresetIds(matchedPresetIds);
  }, [isOpen, editingItem, presetData, formData.customize]);

  const validateGroupDraft = () => {
    const errors: string[] = [];

    if (!groupDraft.name.trim()) {
      errors.push("Group name is required.");
    }

    if (!groupDraft.choices.length) {
      errors.push("At least one choice is required.");
    }

    groupDraft.choices.forEach((choice, index) => {
      if (!choice.label?.trim()) {
        errors.push(`Choice ${index + 1}: Label is required.`);
      }
      if (
        choice.price === undefined ||
        choice.price === null ||
        choice.price < 0
      ) {
        errors.push(`Choice ${index + 1}: Price must be 0 or greater.`);
      }
    });

    // if (groupDraft.choices.filter((c) => c.default).length !== 1) {
    //   errors.push("Required group must have exactly one default choice.");
    // }

    setGroupErrors(errors);
    return errors.length === 0;
  };

  const updateLocalLocationSetting = (
    hotelId: string,
    field: keyof LocationSettings,
    value: any,
  ) => {
    setLocationSettingsMap((prev) => ({
      ...prev,
      [hotelId]: {
        ...(prev[hotelId] ?? { price: null, availability: true, fulfillmentMethods: ["pickup", "delivery", "self-ordering"] as FulfillmentMethod[] }),
        [field]: value,
      },
    }));
  };

  const saveLocationSetting = async (hotelId: string, overrideSetting?: LocationSettings) => {
    if (!editingItem) return;
    const setting = overrideSetting ?? locationSettingsMap[hotelId];
    if (!setting) return;
    setSavingLocation(hotelId);
    try {
      await updateRestaurantMenuItem(hotelId, editingItem._id, {
        ...(readOnly ? {} : { price: setting.price }),
        availability: setting.availability,
        fulfillmentMethods: setting.fulfillmentMethods.length > 0 ? setting.fulfillmentMethods : null,
      });
    } catch {
      toast.error("Failed to save location settings");
    } finally {
      setSavingLocation(null);
    }
  };

  const toggleLocationFulfillmentMethod = (hotelId: string, method: FulfillmentMethod) => {
    const currentSetting = locationSettingsMap[hotelId] ?? {
      price: null,
      availability: true,
      fulfillmentMethods: ["pickup", "delivery", "self-ordering"] as FulfillmentMethod[],
    };
    const updatedMethods = currentSetting.fulfillmentMethods.includes(method)
      ? currentSetting.fulfillmentMethods.filter((m) => m !== method)
      : [...currentSetting.fulfillmentMethods, method];
    const updatedSetting = { ...currentSetting, fulfillmentMethods: updatedMethods };
    setLocationSettingsMap((prev) => ({ ...prev, [hotelId]: updatedSetting }));
    saveLocationSetting(hotelId, updatedSetting);
  };

  // Main category the item is saved under (defaults to the category the dialog was opened from)
  const mainCategoryId = formData.categoryId || categoryId;
  const mainCategoryName =
    allCategories.find((c) => c._id === mainCategoryId)?.name ||
    (mainCategoryId === categoryId ? categoryName : editingItem?.category || categoryName);

  const changeMainCategory = (newId: string) => {
    const cat = allCategories.find((c) => c._id === newId);
    const newDefaultCode =
      (typeof cat?.categoryCode === "object" ? cat.categoryCode?._id : cat?.categoryCode) || "";
    setFormData((prev) => ({
      ...prev,
      categoryId: newId,
      additionalCategoryIds: prev.additionalCategoryIds.filter((id) => id !== newId),
      // Follow the new category's default code unless the code was overridden for this item
      categoryCode:
        !prev.categoryCode || prev.categoryCode === categoryDefaultCodeId
          ? newDefaultCode || prev.categoryCode
          : prev.categoryCode,
    }));
    if (newDefaultCode) setCategoryDefaultCodeId(newDefaultCode);
  };

  const hotelsToShow = isMasterAdmin
    ? hotels
    : hotels.filter((h) => h._id === myHotelId);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (readOnly) return;

    if (!formData.name.trim()) {
      toast.error("Please enter a menu item name");
      return;
    }

    if (!formData.price || parseFloat(formData.price) <= 0) {
      toast.error("Please enter a valid price");
      return;
    }

    setSubmitting(true);

    try {
      const imageUrl = await uploadImage();
      const tagsArray = formData.tags
        .split(",")
        .map((tag) => tag.trim())
        .filter((tag) => tag !== "");

      const menuItemData = {
        name: formData.name.trim(),
        price: parseFloat(formData.price),
        categoryCode: formData.categoryCode || null,
        description: formData.description.trim() || undefined,
        foodType: formData.foodType,
        fulfillmentMethods:
          formData.fulfillmentMethods.length > 0
            ? formData.fulfillmentMethods
            : undefined,
        tags: tagsArray.length > 0 ? tagsArray : undefined,
        categoryId: mainCategoryId,
        category: mainCategoryName,
        additionalCategoryIds: formData.additionalCategoryIds.filter(
          (id) => id !== mainCategoryId,
        ),
        imageUrl: imageUrl || formData.imageUrl || "",
        customize:
          formData.customize.length > 0 ? formData.customize : undefined,
        kitchenName: formData.kitchenName.trim() || undefined,
        prepTime: formData.prepTime || 10,
      };

      let response;
      if (editingItem) {
        // Update existing menu item
        response = await api.put(`/menu/${editingItem._id}`, menuItemData);
      } else {
        // Create new menu item
        response = await api.post("/menu", menuItemData);
      }

      if (response.data.success) {
        // For new items, apply any location settings that were configured
        if (!editingItem) {
          const newMenuId = response.data.data._id;
          const locationEntries = Object.entries(locationSettingsMap);
          if (locationEntries.length > 0) {
            try {
              await Promise.allSettled(
                locationEntries.map(([hotelId, setting]) =>
                  updateRestaurantMenuItem(hotelId, newMenuId, {
                    price: setting.price,
                    availability: setting.availability,
                    fulfillmentMethods:
                      setting.fulfillmentMethods.length > 0 ? setting.fulfillmentMethods : null,
                  }),
                ),
              );
            } catch (err) {
              console.error("Failed to apply location settings:", err);
            }
          }
        }

        toast.success(
          editingItem
            ? "Menu item updated successfully!"
            : "Menu item added successfully!",
        );
        resetForm();
        onMenuItemAdded();
        onClose();
      } else {
        toast.error(
          response.data.message ||
            `Failed to ${editingItem ? "update" : "add"} menu item`,
        );
      }
    } catch (err: any) {
      console.error(
        `Failed to ${editingItem ? "update" : "add"} menu item:`,
        err,
      );
      toast.error(
        err.response?.data?.message ||
          `Failed to ${editingItem ? "update" : "add"} menu item`,
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-[60] flex items-center justify-center p-2 sm:p-4">
        <div
          className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={handleClose}
        />

        <div className="relative bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[95dvh] sm:max-h-[90vh] flex flex-col">
          {/* Modal Header */}
          <div className="bg-white border-b border-gray-200 px-4 sm:px-6 py-4 rounded-t-lg flex justify-between items-center flex-shrink-0 gap-3">
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-gray-800">
                {readOnly
                  ? "Menu Item Details"
                  : editingItem
                    ? "Edit Menu Item"
                    : "Add Menu Item"}
              </h2>
              <p className="text-sm text-gray-500">Category: {mainCategoryName}</p>
            </div>
            <button
              type="button"
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          {/* Modal Content */}
          <div className="flex-1 overflow-y-auto no-scrollbar">
            <form id="add-menu-form" onSubmit={handleSubmit} className="p-4 sm:p-6">
              <div className="space-y-4">
                <fieldset disabled={readOnly} className="min-w-0 space-y-4">
                {/* Name & Price */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      Item Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      placeholder="e.g., Margherita Pizza"
                      required
                      className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    />
                  </div>
                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      Price *
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                        $
                      </span>
                      <input
                        type="number"
                        value={formData.price}
                        onChange={(e) =>
                          setFormData({ ...formData, price: e.target.value })
                        }
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                        required
                        className="w-full p-2.5 pl-8 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      />
                    </div>
                  </div>
                </div>

                {/* Main Category */}
                {allCategories.length > 0 && (
                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      Main Category
                    </label>
                    <select
                      className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white"
                      value={mainCategoryId}
                      onChange={(e) => changeMainCategory(e.target.value)}
                    >
                      {!allCategories.some((c) => c._id === mainCategoryId) && (
                        <option value={mainCategoryId}>{mainCategoryName}</option>
                      )}
                      {allCategories.map((cat) => (
                        <option key={cat._id} value={cat._id}>
                          {cat.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Additional Categories */}
                {allCategories.some((c) => c._id !== mainCategoryId) && (
                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      Also show in categories
                      <span className="text-gray-400 text-xs ml-2">
                        (item appears under {mainCategoryName} and each selected category)
                      </span>
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {allCategories
                        .filter((c) => c._id !== mainCategoryId)
                        .map((cat) => {
                          const selected = formData.additionalCategoryIds.includes(cat._id);
                          return (
                            <button
                              key={cat._id}
                              type="button"
                              onClick={() =>
                                setFormData((prev) => ({
                                  ...prev,
                                  additionalCategoryIds: selected
                                    ? prev.additionalCategoryIds.filter((id) => id !== cat._id)
                                    : [...prev.additionalCategoryIds, cat._id],
                                }))
                              }
                              className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                                selected
                                  ? "bg-blue-600 text-white border-blue-600"
                                  : "bg-white text-gray-700 border-gray-300 hover:border-blue-400"
                              }`}
                            >
                              {cat.name}
                            </button>
                          );
                        })}
                    </div>
                  </div>
                )}

                {/* Category Code */}
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Category Code
                    <span className="text-gray-400 text-xs ml-2">
                      (pre-filled from category, change to override for this item)
                    </span>
                  </label>
                  <select
                    className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white"
                    value={formData.categoryCode}
                    onChange={(e) =>
                      setFormData({ ...formData, categoryCode: e.target.value })
                    }
                  >
                    <option value="">-- Select a category code --</option>
                    {categoryCodes.map((code) => (
                      <option key={code._id} value={code._id}>
                        {code.code} – {code.name}
                        {code.taxable ? " (Taxable)" : " (Non-taxable)"}
                      </option>
                    ))}
                  </select>
                  {(() => {
                    const selected = categoryCodes.find(
                      (c) => c._id === formData.categoryCode,
                    );
                    if (!selected) return null;
                    const isOverride =
                      categoryDefaultCodeId &&
                      selected._id !== categoryDefaultCodeId;
                    return (
                      <p
                        className={`text-xs mt-1 font-medium ${selected.taxable ? "text-orange-600" : "text-green-600"}`}
                      >
                        {selected.taxable ? "Tax will be applied" : "No tax"}{" "}
                        on this item
                        {isOverride && (
                          <span className="ml-2 text-blue-500">
                            (overriding category default)
                          </span>
                        )}
                      </p>
                    );
                  })()}
                </div>

                {/* Kitchen Name */}
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Kitchen Name{" "}
                    <span className="text-gray-500 text-xs ml-2">
                      (optional)
                    </span>
                  </label>
                  <input
                    type="text"
                    value={formData.kitchenName}
                    onChange={(e) =>
                      setFormData({ ...formData, kitchenName: e.target.value })
                    }
                    placeholder="e.g., Main Kitchen, Grill Station"
                    className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  />
                </div>

                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Item prep time{" "}
                  </label>
                  <input
                    type="text"
                    value={formData.prepTime}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        prepTime: parseInt(e.target.value) || 0,
                      })
                    }
                    placeholder="10 minutes"
                    className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  />
                </div>

                {/* Food Type */}
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Food Type *
                  </label>
                  <div className="flex flex-wrap gap-4 sm:gap-6 items-center">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="foodType"
                        value="veg"
                        checked={formData.foodType === "veg"}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            foodType: e.target.value as any,
                          })
                        }
                        required
                      />
                      <img
                        src={vegIcon}
                        alt="veg"
                        className="inline-block w-4 h-4 ml-1"
                      />
                      <span>Vegetarian</span>
                    </label>

                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="foodType"
                        value="non-veg"
                        checked={formData.foodType === "non-veg"}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            foodType: e.target.value as any,
                          })
                        }
                      />
                      <img
                        src={nonvegIcon}
                        alt="non-veg"
                        className="inline-block w-4 h-4 ml-1"
                      />
                      <span>Non-Vegetarian</span>
                    </label>

                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="foodType"
                        value="both"
                        checked={formData.foodType === "both"}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            foodType: e.target.value as any,
                          })
                        }
                      />
                      <img
                        src={both}
                        alt="both"
                        className="inline-block w-4 h-4 ml-1"
                      />
                      <span>Both</span>
                    </label>
                  </div>
                </div>

                {/* Fulfillment Methods */}
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Edit online fulfillment methods
                  </label>
                  <div className="flex flex-wrap gap-4 sm:gap-6 items-center">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        value="pickup"
                        checked={formData.fulfillmentMethods.includes("pickup")}
                        onChange={(e) => {
                          const value = e.target.value as
                            | "pickup"
                            | "delivery"
                            | "self-ordering";
                          setFormData({
                            ...formData,
                            fulfillmentMethods: e.target.checked
                              ? [...formData.fulfillmentMethods, value]
                              : formData.fulfillmentMethods.filter(
                                  (m) => m !== value,
                                ),
                          });
                        }}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span>Pickup</span>
                    </label>

                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        value="delivery"
                        checked={formData.fulfillmentMethods.includes(
                          "delivery",
                        )}
                        onChange={(e) => {
                          const value = e.target.value as
                            | "pickup"
                            | "delivery"
                            | "self-ordering";
                          setFormData({
                            ...formData,
                            fulfillmentMethods: e.target.checked
                              ? [...formData.fulfillmentMethods, value]
                              : formData.fulfillmentMethods.filter(
                                  (m) => m !== value,
                                ),
                          });
                        }}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span>Delivery</span>
                    </label>

                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        value="self-ordering"
                        checked={formData.fulfillmentMethods.includes(
                          "self-ordering",
                        )}
                        onChange={(e) => {
                          const value = e.target.value as
                            | "pickup"
                            | "delivery"
                            | "self-ordering";
                          setFormData({
                            ...formData,
                            fulfillmentMethods: e.target.checked
                              ? [...formData.fulfillmentMethods, value]
                              : formData.fulfillmentMethods.filter(
                                  (m) => m !== value,
                                ),
                          });
                        }}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span>Self-Ordering</span>
                    </label>
                  </div>
                </div>

                </fieldset>

                {/* Location Settings */}
                {hotelsToShow.length > 0 && (
                  <div>
                    <div className="flex items-center gap-1.5 mb-2">
                      <MapPin size={14} className="text-gray-400" />
                      <span className="text-sm font-medium text-gray-700">Location Settings</span>
                      {!editingItem && (
                        <span className="text-xs text-gray-400 ml-1">(applied when item is saved)</span>
                      )}
                    </div>
                    <div className="border border-gray-200 rounded-lg divide-y divide-gray-200 overflow-hidden">
                      {hotelsToShow.map((hotel) => {
                        const setting = locationSettingsMap[hotel._id] ?? {
                          price: null,
                          availability: true,
                          fulfillmentMethods: ["pickup", "delivery", "self-ordering"] as FulfillmentMethod[],
                        };
                        const isExpanded = expandedHotelRow === hotel._id;
                        const isSaving = savingLocation === hotel._id;

                        return (
                          <div key={hotel._id}>
                            {/* Row header */}
                            <button
                              type="button"
                              onClick={() => setExpandedHotelRow(isExpanded ? null : hotel._id)}
                              className="w-full flex items-center justify-between px-3 py-2.5 bg-white hover:bg-gray-50 transition-colors text-left"
                            >
                              <div className="flex items-center gap-2 min-w-0">
                                <span
                                  className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${setting.availability ? "bg-green-500" : "bg-gray-400"}`}
                                />
                                <span className="text-sm font-medium text-gray-800 truncate">{hotel.hotelName}</span>
                                {isSaving && (
                                  <div className="w-3 h-3 border border-gray-400 border-t-transparent rounded-full animate-spin flex-shrink-0" />
                                )}
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                                <span className="text-xs text-gray-400 hidden sm:block">
                                  {setting.fulfillmentMethods.length > 0
                                    ? setting.fulfillmentMethods.map((m) => FULFILLMENT_METHOD_LABELS[m]).join(" · ")
                                    : "No methods set"}
                                </span>
                                {setting.price !== null && (
                                  <span className="text-xs text-gray-400">{cs()}{setting.price}</span>
                                )}
                                <ChevronDown
                                  size={14}
                                  className={`text-gray-400 transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`}
                                />
                              </div>
                            </button>

                            {/* Expanded content — slide animation */}
                            <div
                              className={`grid transition-all duration-300 ease-in-out ${
                                isExpanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                              }`}
                            >
                              <div className="min-h-0 overflow-hidden">
                              <div className="px-3 py-4 bg-gray-50 border-t border-gray-100 space-y-4">
                                {/* Fulfillment Methods */}
                                <div>
                                  <p className="text-xs font-medium text-gray-500 mb-2">Fulfillment Methods</p>
                                  <div className="flex flex-wrap gap-x-6 gap-y-2">
                                    {(["pickup", "delivery", "self-ordering"] as FulfillmentMethod[]).map((key) => (
                                      <label key={key} className="flex items-center gap-2 cursor-pointer select-none">
                                        <input
                                          type="checkbox"
                                          checked={setting.fulfillmentMethods.includes(key)}
                                          onChange={() => toggleLocationFulfillmentMethod(hotel._id, key)}
                                          className="w-4 h-4 rounded border-gray-300"
                                        />
                                        <span className="text-sm text-gray-700">{FULFILLMENT_METHOD_LABELS[key]}</span>
                                      </label>
                                    ))}
                                  </div>
                                </div>

                                {/* Price Override */}
                                <div>
                                  <label className="text-xs font-medium text-gray-500 block mb-1.5">
                                    Price Override
                                    <span className="ml-1 font-normal text-gray-400">(blank = item default)</span>
                                  </label>
                                  <div className="relative w-36">
                                    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm select-none">$</span>
                                    <input
                                      type="number"
                                      min="0"
                                      step="0.01"
                                      value={setting.price ?? ""}
                                      onChange={(e) =>
                                        updateLocalLocationSetting(
                                          hotel._id,
                                          "price",
                                          e.target.value === "" ? null : Number(e.target.value),
                                        )
                                      }
                                      onBlur={() => saveLocationSetting(hotel._id)}
                                      placeholder={formData.price || "0.00"}
                                      disabled={readOnly}
                                      className="w-full pl-6 pr-2 py-1.5 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-1 focus:ring-gray-400 focus:border-gray-400 disabled:bg-gray-100 disabled:text-gray-500"
                                    />
                                  </div>
                                </div>

                                {/* Availability */}
                                <div className="flex items-center justify-between">
                                  <span className="text-xs font-medium text-gray-500">
                                    {setting.availability ? "Available at this location" : "Hidden at this location"}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const newAvailability = !setting.availability;
                                      const updatedSetting = { ...setting, availability: newAvailability };
                                      setLocationSettingsMap((prev) => ({ ...prev, [hotel._id]: updatedSetting }));
                                      saveLocationSetting(hotel._id, updatedSetting);
                                    }}
                                    className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ${
                                      setting.availability ? "bg-gray-800" : "bg-gray-300"
                                    }`}
                                  >
                                    <span
                                      className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition duration-200 ${
                                        setting.availability ? "translate-x-4" : "translate-x-0"
                                      }`}
                                    />
                                  </button>
                                </div>
                              </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <fieldset disabled={readOnly} className="min-w-0 space-y-4">
                {/* Description */}
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    placeholder="Describe your menu item..."
                    rows={3}
                    className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
                  />
                </div>

                {/* Tags */}
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Tags{" "}
                    <span className="text-gray-500 text-xs ml-2">
                      (separate by commas)
                    </span>
                  </label>
                  <input
                    type="text"
                    value={formData.tags}
                    onChange={(e) =>
                      setFormData({ ...formData, tags: e.target.value })
                    }
                    placeholder="e.g., spicy, popular, chef's special"
                    className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  />
                </div>

                {/* Image Upload */}
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    {readOnly
                      ? "Image"
                      : editingItem
                        ? "Upload New Image"
                        : "Upload Image *"}
                  </label>
                  {formData.imageUrl && (
                    <div className="mb-3">
                      <p className="text-sm text-gray-500 mb-2">
                        Current image:
                      </p>
                      <img
                        src={formData.imageUrl}
                        alt="Current"
                        className="w-24 h-24 object-cover rounded-md border"
                      />
                    </div>
                  )}
                  {!readOnly && (
                  <input
                    type="file"
                    accept="image/png, image/jpeg, image/jpg, image/webp"
                    onChange={(e) =>
                      setFile(e.target.files ? e.target.files[0] : null)
                    }
                    required={!formData.imageUrl && !editingItem}
                    className="w-full p-2.5 border-2 border-dashed border-gray-300 rounded-md focus:border-blue-500 transition-all file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer"
                  />
                  )}
                  {uploading && (
                    <p className="text-blue-600 text-sm mt-2">
                      Uploading image...
                    </p>
                  )}
                </div>

                {/* Customizations Section */}
                <div className="mt-6 border-t border-gray-200 pt-6">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-800">
                      Customizations
                    </h3>
                    {!readOnly && (
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setEditingGroupIndex(null);
                          setGroupDraft({
                            name: "",
                            required: false,
                            multiple: false,
                            multipleAddons: false,
                            choices: [],
                            noOfFreeToppings: undefined,
                            maxToppingsAllowed: undefined,
                            minAddons: undefined,
                            customized: true,
                            displayType: "checkbox",
                            quantityOptions: [0, 1, 2, 3],
                            portionOptions: [
                              { key: "left", label: "Left" },
                              { key: "whole", label: "Whole" },
                              { key: "right", label: "Right" },
                            ],
                            showPortionSelector: false,
                          });
                          setGroupErrors([]);
                          setShowGroupModal(true);
                        }}
                        className="bg-blue-600 text-white px-4 py-2 rounded-md flex items-center justify-center gap-2 text-sm w-full sm:w-auto"
                      >
                        <Plus size={16} />
                        Add Group
                      </button>

                      {presetData.length > 0 && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setOpen(true);
                          }}
                          className="bg-blue-600 text-white px-4 py-2 rounded-md flex items-center justify-center gap-2 text-sm w-full sm:w-auto"
                        >
                          <Plus size={16} />
                          {selectedPresetIds.length > 0
                            ? `Add from presets (${selectedPresetIds.length})`
                            : "Add from presets"}
                        </button>
                      )}
                    </div>
                    )}
                  </div>

                  {formData.customize.length === 0 ? (
                    <div className="text-center py-6 bg-gray-50 rounded-md border-2 border-dashed border-gray-300">
                      <p className="text-gray-500 text-sm">
                        No customization groups added yet
                      </p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto border rounded-lg">
                      <table className="min-w-full text-sm">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="px-4 py-3 text-left">Group Name</th>
                            <th className="px-4 py-3 text-left">Type</th>
                            <th className="px-4 py-3 text-left">Limits</th>
                            {!readOnly && (
                              <th className="px-4 py-3 text-right">Actions</th>
                            )}
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {formData.customize.map((group, index) => (
                            <tr key={index}>
                              <td className="px-4 py-3 font-medium">
                                {group.name}
                              </td>
                              <td className="px-4 py-3 text-gray-600">
                                {group.required
                                  ? "Required (Single)"
                                  : group.multiple
                                    ? "Optional (Multiple)"
                                    : "Optional (Single)"}
                              </td>
                              <td className="px-4 py-3 text-gray-500 text-xs space-y-0.5">
                                {(group.minAddons ?? 0) > 0 && (
                                  <div>Min: {group.minAddons}</div>
                                )}
                                {group.maxToppingsAllowed !== undefined && (
                                  <div>Max: {group.maxToppingsAllowed}</div>
                                )}
                                {(group.noOfFreeToppings ?? 0) > 0 && (
                                  <div className="text-green-600">Free: {group.noOfFreeToppings}</div>
                                )}
                                {!(group.minAddons ?? 0) && group.maxToppingsAllowed === undefined && !(group.noOfFreeToppings ?? 0) && (
                                  <span className="text-gray-300">—</span>
                                )}
                              </td>
                              {!readOnly && (
                              <td className="px-4 py-3 flex justify-end gap-2">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setEditingGroupIndex(index);
                                    if (
                                      group.multiple === false &&
                                      group.required === false &&
                                      !group.multipleAddons
                                    ) {
                                      setGroupDraft({
                                        ...group,
                                        customized: true,
                                      });
                                    } else {
                                      setGroupDraft(group);
                                    }
                                    setGroupErrors([]);
                                    setShowGroupModal(true);
                                  }}
                                  className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-md"
                                >
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    const updated = [...formData.customize];
                                    updated.splice(index, 1);
                                    setFormData({
                                      ...formData,
                                      customize: updated,
                                    });
                                  }}
                                  className="px-3 py-1.5 bg-red-50 text-red-600 rounded-md"
                                >
                                  Delete
                                </button>
                              </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
                </fieldset>
              </div>
            </form>
          </div>

          {/* Modal Footer */}
          <div className="bg-gray-50 border-t border-gray-200 px-4 sm:px-6 py-4 rounded-b-lg flex flex-col-reverse sm:flex-row justify-end gap-3 flex-shrink-0">
            <button
              type="button"
              onClick={handleClose}
              className="w-full sm:w-auto px-5 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition"
            >
              {readOnly ? "Close" : "Cancel"}
            </button>
            {!readOnly && (
              <button
                type="submit"
                form="add-menu-form"
                className="w-full sm:w-auto px-5 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition disabled:opacity-50"
                disabled={submitting || uploading}
              >
                {submitting || uploading
                  ? editingItem
                    ? "Updating..."
                    : "Adding..."
                  : editingItem
                    ? "Update Item"
                    : "Add Item"}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Group Modal */}
      {showGroupModal && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-2 sm:p-4">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => setShowGroupModal(false)}
          />

          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[95dvh] sm:max-h-[90vh] flex flex-col">
            <div className="border-b px-4 sm:px-6 py-4 flex justify-between items-center">
              <h3 className="text-lg font-semibold">
                {editingGroupIndex !== null ? "Edit Group" : "Add Group"}
              </h3>
              <button onClick={() => setShowGroupModal(false)}>
                <X size={18} />
              </button>
            </div>

            <div className="p-4 sm:p-6 space-y-4 overflow-y-auto">
              {groupErrors.length > 0 && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-md text-sm">
                  {groupErrors.map((err, i) => (
                    <p key={i}>{err}</p>
                  ))}
                </div>
              )}

              <input
                className="w-full p-2.5 border rounded-md"
                placeholder="Group Name (e.g. Size, Cheese)"
                value={groupDraft.name}
                onChange={(e) =>
                  setGroupDraft({ ...groupDraft, name: e.target.value })
                }
              />

              <div className="flex flex-wrap items-center gap-4 sm:gap-8">
                <div className="relative flex items-center gap-1 group">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={groupDraft.required}
                      onChange={(e) =>
                        setGroupDraft({
                          ...groupDraft,
                          required: e.target.checked,
                          multiple: e.target.checked
                            ? false
                            : groupDraft.multiple,
                          customized: e.target.checked
                            ? false
                            : groupDraft.customized,
                          multipleAddons: e.target.checked
                            ? false
                            : groupDraft.multipleAddons,
                        })
                      }
                    />
                    Required
                  </label>

                  <div
                    className="absolute left-0 top-full mt-2 hidden group-hover:block z-[9999]
                    w-56 rounded-md bg-black text-white text-xs px-3 py-2 shadow-lg"
                  >
                    Allows user to select <b>only one</b> option from choices
                  </div>
                </div>

                <div className="relative flex items-center gap-1 group">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={groupDraft.multiple || false}
                      onChange={(e) =>
                        setGroupDraft({
                          ...groupDraft,
                          multiple: e.target.checked,
                          required: e.target.checked
                            ? false
                            : groupDraft.required,
                          customized: e.target.checked
                            ? false
                            : groupDraft.customized,
                          multipleAddons: e.target.checked
                            ? false
                            : groupDraft.multipleAddons,
                        })
                      }
                    />
                    Multiple Choice
                  </label>

                  <div
                    className="absolute left-0 top-full mt-2 hidden group-hover:block z-[9999]
                    w-64 rounded-md bg-black text-white text-xs px-3 py-2 shadow-lg"
                  >
                    Allows user to select <b>multiple</b> options from choices
                  </div>
                </div>

                {/* {!groupDraft.required && !groupDraft.multiple && (
                  <div className="relative flex items-center gap-1 group text-sm text-gray-500">
                    <Info size={14} className="text-gray-400" />
                    <span>Optional (Single)</span>
                  </div>
                )} */}

                <div className="relative flex items-center gap-1 group">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={groupDraft.customized || false}
                      onChange={(e) =>
                        setGroupDraft({
                          ...groupDraft,
                          customized: e.target.checked,
                          required: e.target.checked
                            ? false
                            : groupDraft.required,
                          multiple: e.target.checked
                            ? false
                            : groupDraft.multiple,
                          multipleAddons: e.target.checked
                            ? false
                            : groupDraft.multipleAddons,
                        })
                      }
                    />
                    0,1,2 Customization
                  </label>

                  <div
                    className="absolute left-0 top-full mt-2 hidden group-hover:block z-[9999]
                      w-72 rounded-md bg-black text-white text-xs px-3 py-2 shadow-lg"
                  >
                    If any option is not selected, allows user to choose the{" "}
                    <b>quantity/count</b> of a particular choice
                  </div>
                </div>

                {/* Multiple Addons */}
                <div className="relative flex items-center gap-1 group">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={groupDraft.multipleAddons || false}
                      onChange={(e) =>
                        setGroupDraft({
                          ...groupDraft,
                          multipleAddons: e.target.checked,
                          required: e.target.checked ? false : groupDraft.required,
                          multiple: e.target.checked ? false : groupDraft.multiple,
                          customized: e.target.checked ? false : groupDraft.customized,
                        })
                      }
                    />
                    Multiple Addons
                  </label>

                  <div
                    className="absolute left-0 top-full mt-2 hidden group-hover:block z-[9999]
                      w-72 rounded-md bg-black text-white text-xs px-3 py-2 shadow-lg"
                  >
                    Each choice gets its own <b>- qty +</b> stepper. Use for items like dips where the user can add multiple of the same item.
                  </div>
                </div>
              </div>

              {/* Addon Limits - show for all non-required groups */}
              {!groupDraft.required && (
              <div className="border rounded-lg p-4 bg-gray-50">
                <h4 className="text-sm font-semibold mb-3">Addon Limits</h4>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Min Addons
                      <span className="text-gray-400 ml-1">(0 = optional)</span>
                    </label>
                    <input
                      type="number"
                      min="0"
                      placeholder="0"
                      className="w-full p-2 border rounded-md text-sm"
                      value={groupDraft.minAddons ?? ""}
                      onChange={(e) =>
                        setGroupDraft({
                          ...groupDraft,
                          minAddons: e.target.value !== "" ? parseInt(e.target.value, 10) : undefined,
                        })
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Max Addons
                      <span className="text-gray-400 ml-1">(blank = unlimited)</span>
                    </label>
                    <input
                      type="number"
                      min="0"
                      placeholder="unlimited"
                      className={`w-full p-2 border rounded-md text-sm ${
                        groupDraft.maxToppingsAllowed !== undefined &&
                        groupDraft.noOfFreeToppings !== undefined &&
                        groupDraft.maxToppingsAllowed <= groupDraft.noOfFreeToppings
                          ? "border-red-400"
                          : "border-gray-300"
                      }`}
                      value={groupDraft.maxToppingsAllowed ?? ""}
                      onChange={(e) =>
                        setGroupDraft({
                          ...groupDraft,
                          maxToppingsAllowed: e.target.value !== "" ? parseInt(e.target.value, 10) : undefined,
                        })
                      }
                    />
                    {groupDraft.maxToppingsAllowed !== undefined &&
                      groupDraft.noOfFreeToppings !== undefined &&
                      groupDraft.maxToppingsAllowed <= groupDraft.noOfFreeToppings && (
                        <p className="text-xs text-red-500 mt-1">Must be greater than free items</p>
                      )}
                  </div>
                  {!groupDraft.multiple && (
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Free Items
                        <span className="text-gray-400 ml-1">(included free)</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        placeholder="0"
                        className="w-full p-2 border rounded-md text-sm"
                        value={groupDraft.noOfFreeToppings ?? ""}
                        onChange={(e) =>
                          setGroupDraft({
                            ...groupDraft,
                            noOfFreeToppings: e.target.value !== "" ? parseInt(e.target.value, 10) : undefined,
                          })
                        }
                      />
                    </div>
                  )}
                </div>
              </div>
              )}

              {/* Display Settings - Only show when 0,1,2 Customization is selected */}
              {groupDraft.customized && (
                <div className="border rounded-lg p-4 bg-gray-50">
                  <h4 className="text-sm font-semibold mb-3">
                    Display Settings
                  </h4>

                  {/* Quantity Options */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Quantity Options (comma separated numbers)
                    </label>
                    <input
                      className="w-full p-2.5 border rounded-md"
                      placeholder="e.g., 0, 1, 2, 3 or -1, 0, 1, 2"
                      value={(groupDraft.quantityOptions || []).join(", ")}
                      onChange={(e) => {
                        const values = e.target.value
                          .split(",")
                          .map((v) => parseInt(v.trim()))
                          .filter((v) => !isNaN(v));
                        setGroupDraft({
                          ...groupDraft,
                          quantityOptions:
                            values.length > 0 ? values : [0, 1, 2, 3],
                        });
                      }}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      For default toppings use -1, 0, 1, 2 (where -1 means
                      "none", 0 is default). For extras use 0, 1, 2, 3.
                    </p>
                  </div>

                  {/* Show Portion Selector Toggle */}
                  <div className="mb-4">
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={groupDraft.showPortionSelector || false}
                        onChange={(e) =>
                          setGroupDraft({
                            ...groupDraft,
                            showPortionSelector: e.target.checked,
                          })
                        }
                      />
                      Show Portion Selector (Left/Right/Whole for Half & Half
                      pizzas)
                    </label>
                  </div>

                  {/* Portion Options - Only show when showPortionSelector is true */}
                  {groupDraft.showPortionSelector && (
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Portion Options
                      </label>
                      <div className="space-y-2">
                        {(groupDraft.portionOptions || []).map(
                          (option, idx) => (
                            <div key={idx} className="flex flex-col sm:flex-row gap-2 sm:items-center">
                              <input
                                className="flex-1 p-2 border rounded-md text-sm"
                                placeholder="Key (e.g., left)"
                                value={option.key}
                                onChange={(e) => {
                                  const updated = [
                                    ...(groupDraft.portionOptions || []),
                                  ];
                                  updated[idx] = {
                                    ...updated[idx],
                                    key: e.target.value,
                                  };
                                  setGroupDraft({
                                    ...groupDraft,
                                    portionOptions: updated,
                                  });
                                }}
                              />
                              <input
                                className="flex-1 p-2 border rounded-md text-sm"
                                placeholder="Label (e.g., Left Half)"
                                value={option.label}
                                onChange={(e) => {
                                  const updated = [
                                    ...(groupDraft.portionOptions || []),
                                  ];
                                  updated[idx] = {
                                    ...updated[idx],
                                    label: e.target.value,
                                  };
                                  setGroupDraft({
                                    ...groupDraft,
                                    portionOptions: updated,
                                  });
                                }}
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  const updated = (
                                    groupDraft.portionOptions || []
                                  ).filter((_, i) => i !== idx);
                                  setGroupDraft({
                                    ...groupDraft,
                                    portionOptions: updated,
                                  });
                                }}
                                className="text-red-600 text-sm px-2"
                              >
                                Remove
                              </button>
                            </div>
                          ),
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            setGroupDraft({
                              ...groupDraft,
                              portionOptions: [
                                ...(groupDraft.portionOptions || []),
                                { key: "", label: "" },
                              ],
                            });
                          }}
                          className="text-sm text-blue-600 hover:text-blue-800"
                        >
                          + Add Portion Option
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Choices Table */}
              <div className="border rounded-lg overflow-x-auto">
                <table className="min-w-[700px] sm:min-w-full text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-2 text-left">Choice</th>
                      <th className="px-4 py-2 text-left">Price</th>
                      <th className="px-4 py-2 text-center">Default</th>
                      <th className="px-4 py-2 text-left">Image</th>
                      <th className="px-4 py-2 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {groupDraft.choices.map((choice, index) => (
                      <tr key={index}>
                        <td className="px-4 py-2">
                          <input
                            className="w-full border p-2 rounded"
                            placeholder="Label"
                            value={choice.label}
                            onChange={(e) => {
                              const updated = [...groupDraft.choices];
                              updated[index].label = e.target.value;
                              setGroupDraft({
                                ...groupDraft,
                                choices: updated,
                              });
                            }}
                          />
                        </td>
                        <td className="px-4 py-2">
                          <input
                            type="number"
                            className="w-full border p-2 rounded"
                            placeholder="0.00"
                            value={choice.price === 0 ? "" : choice.price}
                            onChange={(e) => {
                              const val = e.target.value;
                              const updated = [...groupDraft.choices];
                              updated[index].price =
                                val === "" ? 0 : Number(val);
                              setGroupDraft({
                                ...groupDraft,
                                choices: updated,
                              });
                            }}
                          />
                        </td>
                        <td className="px-4 py-2 text-center">
                          <input
                            type="checkbox"
                            checked={choice.default || false}
                            onChange={(e) => {
                              const updated = [...groupDraft.choices];
                              if (
                                e.target.checked &&
                                groupDraft.required
                              ) {
                                updated.forEach((c, i) => {
                                  c.default = i === index;
                                });
                              } else {
                                updated[index].default = e.target.checked;
                              }
                              setGroupDraft({
                                ...groupDraft,
                                choices: updated,
                              });
                            }}
                          />
                        </td>
                        <td className="px-4 py-2">
                          <label className="block">
                            <input
                              type="file"
                              accept="image/png, image/jpeg, image/webp"
                              className="hidden"
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                const key = `group-${editingGroupIndex ?? "new"}-${index}`;
                                try {
                                  const url = await handleChoiceImageUpload(
                                    file,
                                    key,
                                  );
                                  const updated = [...groupDraft.choices];
                                  updated[index].image = url;
                                  setGroupDraft({
                                    ...groupDraft,
                                    choices: updated,
                                  });
                                } catch (err) {
                                  console.error(
                                    "Choice image upload failed",
                                    err,
                                  );
                                }
                              }}
                            />
                            {!choice.image ? (
                              <div className="flex flex-col items-center justify-center gap-1 border-2 border-dashed border-gray-300 rounded-md p-3 text-xs text-gray-500 cursor-pointer hover:border-blue-400">
                                <Upload size={16} className="text-blue-500" />
                                <span>Upload</span>
                              </div>
                            ) : (
                              <div className="relative cursor-pointer w-12 h-12 group">
                                <img
                                  src={choice.image}
                                  alt="choice"
                                  className="w-full h-full object-cover rounded-md border"
                                />
                              </div>
                            )}
                          </label>
                        </td>
                        <td className="px-4 py-2 text-right">
                          <button
                            type="button"
                            onClick={() => {
                              const updated = [...groupDraft.choices];
                              updated.splice(index, 1);
                              setGroupDraft({
                                ...groupDraft,
                                choices: updated,
                              });
                            }}
                            className="text-red-600 text-sm"
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <button
                type="button"
                onClick={() => {
                  setGroupDraft({
                    ...groupDraft,
                    choices: [...groupDraft.choices, { label: "", price: 0 }],
                  });
                }}
                className="w-full py-2 bg-gray-100 rounded-md"
              >
                + Add Choice
              </button>
            </div>

            <div className="border-t px-4 sm:px-6 py-4 flex flex-col-reverse sm:flex-row justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowGroupModal(false)}
                className="w-full sm:w-auto px-4 py-2 bg-gray-200 rounded-md"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  if (!validateGroupDraft()) return;

                  const updated = [...formData.customize];
                  if (editingGroupIndex !== null) {
                    updated[editingGroupIndex] = groupDraft;
                  } else {
                    updated.push(groupDraft);
                  }

                  setFormData({ ...formData, customize: updated });
                  setShowGroupModal(false);
                }}
                className="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-md"
              >
                Save Group
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Prerequisites Dialog */}
      <PrerequisitesDialog
        open={open}
        onOpenChange={setOpen}
        data={presetData}
        onSave={handleSave}
        selectedIds={selectedPresetIds}
      />
    </>
  );
};

export default AddMenuDialog;
